// ── Adapter Supabase do IMeetingRepository ────────────────────────
// Implementa a porta de Reuniões (tabela meetings) usando o cliente Supabase.

import { getSupabase, type TypedSupabaseClient } from '@infra/supabase/client';
import type { MeetingEntry } from '@/types/meeting';
import type { IMeetingRepository, CreateMeetingParams, MeetingDbStatus } from '@modules/agenda';

const requireSupabase = (): TypedSupabaseClient => {
  const s = getSupabase();
  if (!s) throw new Error('Supabase nao inicializado.');
  return s;
};

const SELECT_RICH = `
  *,
  closer:profiles!meetings_closer_id_fkey ( id, nome, initials, color, avatar_url ),
  creator:profiles!meetings_created_by_fkey ( id, nome, initials, color, avatar_url ),
  lead:leads ( id, status )
`;

export class SupabaseMeetingRepository implements IMeetingRepository {
  listMeetingsByLead = async (leadId: string): Promise<MeetingEntry[]> => {
    const supabase = requireSupabase();
    const { data, error } = await supabase
      .from('meetings')
      .select(SELECT_RICH)
      .eq('lead_id', leadId)
      .order('scheduled_at', { ascending: false });
    if (error) throw error;
    return (data || []) as unknown as MeetingEntry[];
  };

  createMeeting = async ({
    workspaceId, leadId, cardId, closerId,
    scheduledAt, durationMinutes = 60, link = null,
  }: CreateMeetingParams): Promise<MeetingEntry> => {
    const supabase = requireSupabase();
    const { data: { user } } = await supabase.auth.getUser();
    const { data, error } = await supabase
      .from('meetings')
      .insert({
        workspace_id: workspaceId,
        lead_id:      leadId,
        card_id:      cardId || null,
        closer_id:    closerId || null,
        scheduled_at: scheduledAt,
        duration_minutes: durationMinutes,
        link,
        status: 'scheduled',
        created_by: user?.id || null,
      })
      .select(SELECT_RICH)
      .single();
    if (error) throw error;
    return data as unknown as MeetingEntry;
  };

  updateMeetingResult = async (
    id: string,
    { status, result }: { status: MeetingDbStatus; result?: string | null },
  ): Promise<MeetingEntry> => {
    const supabase = requireSupabase();
    const { data, error } = await supabase
      .from('meetings')
      .update({ status, result: result ?? null })
      .eq('id', id)
      .select(SELECT_RICH)
      .single();
    if (error) throw error;
    return data as unknown as MeetingEntry;
  };
}
